/**
 * ESCHER FALLS — 二周目。本編を全通水した人に残る、課金しない道。
 *
 * ## 一度廃止した設計である
 * かつて「同じ面に制約を載せて二周目にする」を実装し、遊んで**面白くない**と
 * 判断して丸ごと捨てた（METAGAME-SPEC §5.1）。理由は変わっていない——
 * 錯視パズルの快感は「見え方が変わった瞬間」にあり、空間が同じなら
 * その瞬間は二度目には来ない。締めても増えるのは**制約であって発見ではない**。
 *
 * それでも戻したのは、**役割が変わったから**である。
 * エクストラを課金解放にしたので、払わない人の手元に何も残らなくなった。
 * 二周目は「報酬の頂点」ではなく「払わない人に残る道」として置いてある。
 * この位置づけを忘れて、また本筋の褒美に格上げしないこと。
 *
 * ## 制約は面ごとに計算する
 * 手数の上限を一律にすると、短い面では緩すぎ、長い面では理不尽になる。
 * **その面の最短手数から決める。**
 */

import type { Level, ViewPose, ViewRelation } from '../core/types';
import { isSolvable } from '../core/graph';
import { expandPieces, hasLever } from '../core/pieces';
import { RELATION_COUNT, relationOf } from '../core/projection';

/** 最短手数に上乗せする余裕。0 にすると一手の間違いも許されず、作業になる。 */
export const MOVE_SLACK = 2;

/** 制限時間の下限と、最短手数1手あたりの持ち時間。 */
export const TIME_FLOOR_MS = 40_000;
export const TIME_PER_MOVE_MS = 12_000;

/** 解ける (関係, レバー) の組。見つからなければ null（検証を通っていれば必ずある）。 */
export function solvingCombo(level: Level): { relation: ViewRelation; lever: boolean } | null {
  for (const lever of hasLever(level.pieces) ? [false, true] : [false]) {
    const pieces = expandPieces(level.pieces, lever);
    for (let rel = 0 as ViewRelation; rel < RELATION_COUNT; rel += 1) {
      if (isSolvable(pieces, rel)) return { relation: rel, lever };
    }
  }
  return null;
}

/**
 * 開始視点から解ける視点まで、最短で何手か。レバーを倒す1手も数える。
 *
 * 姿勢は12あるが関係は9で、**真上の4姿勢は同じ関係**になる。
 * だから「関係へ届く姿勢のうち最短のもの」を幅優先で探す必要があり、
 * 関係だけを見て距離を測ることはできない。
 */
export function minMovesFor(level: Level): number {
  const combo = solvingCombo(level);
  if (combo === null) return 0;

  const key = (p: ViewPose): string => `${p.azimuth},${p.elevation}`;
  const seen = new Map<string, number>([[key(level.startView), 0]]);
  const queue: ViewPose[] = [level.startView];
  let viewMoves = 0;
  while (queue.length > 0) {
    const cur = queue.shift() as ViewPose;
    const dist = seen.get(key(cur)) as number;
    if (relationOf(cur) === combo.relation) {
      viewMoves = dist;
      break;
    }
    const next: ViewPose[] = [
      { azimuth: ((((cur.azimuth - 1) % 4) + 4) % 4) as ViewPose['azimuth'], elevation: cur.elevation },
      { azimuth: ((cur.azimuth + 1) % 4) as ViewPose['azimuth'], elevation: cur.elevation },
      { azimuth: cur.azimuth, elevation: Math.min(2, cur.elevation + 1) as ViewPose['elevation'] },
      { azimuth: cur.azimuth, elevation: Math.max(0, cur.elevation - 1) as ViewPose['elevation'] },
    ];
    for (const p of next) {
      if (seen.has(key(p))) continue;
      seen.set(key(p), dist + 1);
      queue.push(p);
    }
  }
  return viewMoves + (combo.lever ? 1 : 0);
}

/**
 * 二周目の面を作る。**元の面は変えない**（同じ面を一周目でも遊ぶため）。
 * 手数と時間の両方を締める。片方だけだと、締めていないほうで抜けられる。
 */
export function toLoop2(level: Level): Level {
  const min = minMovesFor(level);
  return {
    ...level,
    maxMoves: min + MOVE_SLACK,
    timeLimitMs: Math.max(TIME_FLOOR_MS, min * TIME_PER_MOVE_MS),
  };
}

/**
 * 記録の鍵。**二周目の記録を一周目に混ぜない。**
 * 制約つきのタイムを同じ欄に入れると、合計タイムも段位も意味を失う。
 */
export function recordIdFor(levelId: string, loop: number): string {
  return loop >= 2 ? `${levelId}#${loop}` : levelId;
}
